// okta.config.ts
import { Router } from '@angular/router';
import { OktaAuth } from '@okta/okta-auth-js';
import { OKTA_AUTH, OKTA_CONFIG } from '@okta/okta-angular';
import { Capacitor } from '@capacitor/core';

/** ===== Constants ===== */
const OKTA_ISSUER = 'https://osk.okta.com/oauth2/default';

// Client IDs
const OKTA_CLIENT_ID_PWA   = '0oanthzrm8D2w43ew697';  // PWA client for test/prod
const OKTA_CLIENT_ID_NATIVE = '0oasu6hhoxOyhQkrX697';  // native (Capacitor) client

/** ===== Env detection ===== */
const origin = window.location.origin;
const host   = window.location.host;

const isNative      = Capacitor.isNativePlatform();              // running inside Capacitor WebView
const isLiveReload  = isNative && origin.startsWith('http://');  // Capacitor live-reload/dev-server
const isLocalhost   = origin.startsWith('http://localhost');
const isMobileTest  = /(^|\.)(mobile|next)\.test\.iopspro\.com$/i.test(host);
const isMobileProd  = /(^|\.)(mobile|next)\.iopspro\.com$/i.test(host);

/** ===== Redirects & client choice ===== */
const REDIRECT_URI = (isNative && !isLiveReload)
  ? 'com.oshkosh.iops:/login/callback'             // custom scheme for native apps
  : `${origin}/login/callback`;                    // web

const POST_LOGOUT_REDIRECT_URI = (isNative && !isLiveReload)
  ? 'com.oshkosh.iops:/logout'
  : `${origin}/logout`;

const CLIENT_ID =
  (isNative && !isLiveReload) ? OKTA_CLIENT_ID_NATIVE :
  (isLocalhost || isMobileTest || isMobileProd) ? OKTA_CLIENT_ID_PWA :
  OKTA_CLIENT_ID_PWA; // safe fallback for unknown hosts during dev

/** ===== Factory (no inject() here → avoids NG0203) ===== */
export function createOktaAuth(router: Router) {
  // For native + PKCE, query mode is correct; for web PKCE Okta also uses query.
  const responseMode: 'query' | 'fragment' = 'query';

  console.log('[Okta] platform:', isNative ? 'native' : 'web',
    '| liveReload:', isLiveReload,
    '| host:', host,
    '| clientId:', CLIENT_ID,
    '| redirectUri:', REDIRECT_URI);

  const okta = new OktaAuth({
    issuer: OKTA_ISSUER,
    clientId: CLIENT_ID,
    redirectUri: REDIRECT_URI,
    postLogoutRedirectUri: POST_LOGOUT_REDIRECT_URI,
    scopes: ['openid', 'profile', 'email', 'offline_access'],
    pkce: true,
    responseType: ['code'],
    responseMode,
    tokenManager: {
      storage: 'localStorage' as any,
    },
    storageManager: {
      transaction: { storageType: 'localStorage' as any },
      token: { storageType: 'localStorage' as any },
    },
    restoreOriginalUri: async (_oktaAuth, originalUri) => {
      // Always funnel through authenticating; stash the intended target first.
      const target = sessionStorage.getItem('postAuthRedirect') || originalUri || '/organization-summary';
      try {
        sessionStorage.setItem('postAuthRedirect', target);
      } catch (err) {
        console.debug('postAuthRedirect failed', err);
      }
      await router.navigateByUrl('/authenticating', { replaceUrl: true });
    },
  });

  // Ensure Okta services are running so authState$ emits reliably (tokenManager, cross-tab sync, etc).
  void okta.start?.();

  return okta;
}

/** ===== Angular providers ===== */
export const OKTA_PROVIDERS = [
  {
    provide: OKTA_CONFIG,
    useFactory: (router: Router) => ({ oktaAuth: createOktaAuth(router) }),
    deps: [Router],
  },
  { provide: OKTA_AUTH, useFactory: (cfg: any) => cfg.oktaAuth, deps: [OKTA_CONFIG] },
  { provide: OktaAuth,  useExisting: OKTA_AUTH },
];
